// Portfolio Administration v1.0 PR 4a §7.5 — jurisdiction-variance banner.
//
// Spec §7.5: a UCC-1 filed against an organizational debtor must use the
// debtor's state of organization (Article 9 §9-307). If the filing
// jurisdiction differs from that expected value, show a yellow banner.
// The backend emits the same warning at filing time; this is the
// user-visible explanation on the drawer.

import { AlertTriangle } from "lucide-react";
import type { UccFilingDto } from "@/api/portfolioAdmin/types";

interface UccJurisdictionVarianceBannerProps {
  filing: UccFilingDto;
  /** Optional override: skip the heuristic and force-display. Useful for tests. */
  expectedJurisdiction?: string;
}

/** Heuristic: for "Organization" debtors with a JurisdictionOfOrg value, that
 *  state is the expected filing jurisdiction. The DTO doesn't surface
 *  DebtorJurisdictionOfOrg today, so the caller can pass it explicitly via
 *  expectedJurisdiction. When unknown, render nothing. */
export function UccJurisdictionVarianceBanner({
  filing,
  expectedJurisdiction,
}: UccJurisdictionVarianceBannerProps) {
  if (!expectedJurisdiction) return null;
  if (
    filing.filingJurisdiction.toUpperCase() ===
    expectedJurisdiction.toUpperCase()
  ) {
    return null;
  }
  return (
    <div
      className="flex items-start gap-space-2 rounded-md border border-status-warning bg-status-warning/10 px-space-3 py-space-2"
      role="alert"
      data-testid="ucc-jurisdiction-variance"
    >
      <AlertTriangle
        size={16}
        className="text-status-warning shrink-0 mt-[2px]"
        aria-hidden
      />
      <p className="text-small text-onyx m-0">
        Jurisdiction warning: filed in <strong>{filing.filingJurisdiction}</strong>;
        expected <strong>{expectedJurisdiction}</strong> based on the debtor&apos;s
        organization state. A3 (Legal) review recommended.
      </p>
    </div>
  );
}
